/**
 * Pure builders for the dashboard-config export DTO.
 *
 * The UI pulls per-mode `widgets` / `pinnedFilters` from their
 * stores and `layouts` from Dexie, then calls `buildConfig` to
 * produce the on-disk shape. Both modes (normal + compare) are
 * always exported so the bundle restores the full dashboard
 * regardless of which mode the user is currently viewing.
 *
 * Kept store-free so it's trivially testable.
 */
import type {
  DashboardConfig,
  DashboardLayoutItem,
  DashboardModeSlice,
  SerializedCompareConfig,
} from '@/types/dashboard-config';
import { CONFIG_VERSION } from '@/types/dashboard-config';
import type { ColumnMeta, ColumnOverrides } from '@/types/dataset';
import type { ColumnTransform } from '@/types/query';
import type { WidgetConfig } from '@/types/widget';
import type { CompareConfigEntry } from '@/persistence/config-db';

export type ModeSliceInput = {
  widgets: WidgetConfig[];
  pinnedFilters: string[];
  layouts: {
    kpi: DashboardLayoutItem[];
    chart: DashboardLayoutItem[];
  };
};

export type BuildConfigInput = {
  currentColumns: ColumnMeta[];
  modes: {
    normal: ModeSliceInput;
    compare: ModeSliceInput;
  };
  columnOverrides: ColumnOverrides;
  valueGroupings: Record<string, ColumnTransform>;
  /** Active compare-mode configuration (null when never set up). */
  compareConfig: CompareConfigEntry | null;
};

/**
 * Build a `DashboardConfig` DTO from caller-supplied store slices.
 *
 * Widgets are cloned with `datasetId` stripped so the DTO isn't tied
 * to the source dataset's id — the importing session assigns its own.
 */
export function buildConfig(input: BuildConfigInput): DashboardConfig {
  return {
    version: CONFIG_VERSION,
    exportedAt: new Date().toISOString(),
    sourceColumns: input.currentColumns.map((c) => ({
      name: c.name,
      sqlType: c.sqlType,
      ...(c.displayFormat ? { displayFormat: c.displayFormat } : {}),
    })),
    modes: {
      normal: cloneSlice(input.modes.normal),
      compare: cloneSlice(input.modes.compare),
    },
    columnOverrides: { ...input.columnOverrides },
    valueGroupings: cloneTransforms(input.valueGroupings),
    compareConfig: input.compareConfig
      ? serializeCompareConfig(input.compareConfig)
      : null,
  };
}

/** Serialize to a pretty-printed JSON string ready for download. */
export function serializeConfig(config: DashboardConfig): string {
  return JSON.stringify(config, null, 2);
}

function cloneSlice(slice: ModeSliceInput): DashboardModeSlice {
  return {
    widgets: slice.widgets.map(stripDatasetId),
    pinnedFilters: [...slice.pinnedFilters],
    layouts: {
      kpi: slice.layouts.kpi.map((item) => ({ ...item })),
      chart: slice.layouts.chart.map((item) => ({ ...item })),
    },
  };
}

/**
 * Strip `datasetId` from a widget for export. The importing session
 * rewrites it to whatever dataset is active at import time.
 */
function stripDatasetId(w: WidgetConfig): WidgetConfig {
  // Assigning a sentinel empty string is wrong — downstream consumers
  // rebuild WidgetConfig with the real id, but if the import flow
  // forgets, the widget would attach to a dataset called "". Use a
  // recognisable marker so a missed remap blows up early.
  const { datasetId: _drop, ...rest } = w;
  void _drop;
  return { ...rest, datasetId: '__EXPORTED__' };
}

function cloneTransforms(
  groupings: Record<string, ColumnTransform>,
): Record<string, ColumnTransform> {
  const out: Record<string, ColumnTransform> = {};
  for (const [col, t] of Object.entries(groupings)) {
    if (t.kind === 'map') {
      out[col] = {
        kind: 'map',
        column: t.column,
        mapping: { ...t.mapping },
        fallback: t.fallback,
        ...(t.otherLabel !== undefined ? { otherLabel: t.otherLabel } : {}),
      };
    } else {
      out[col] = {
        kind: 'bin',
        column: t.column,
        bins: t.bins.map((b) => ({ ...b })),
        ...(t.otherLabel !== undefined ? { otherLabel: t.otherLabel } : {}),
      };
    }
  }
  return out;
}

function serializeCompareConfig(
  cfg: CompareConfigEntry,
): SerializedCompareConfig {
  return {
    dateColumn: cfg.dateColumn,
    granularity: cfg.granularity,
    periodA: { ...cfg.periodA },
    periodB: { ...cfg.periodB },
  };
}
